import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useRealtime } from "@/lib/realtime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  FileText, Plus, ChevronRight, ChevronDown, Trash2, BookOpen, Search, Eye, Edit3, Save,
  Volume2, VolumeX, Smile, Clock, Pin, Share2, MessageSquare, Code, Table, List, ListOrdered,
  CheckSquare, AlertCircle, Info, CheckCircle2, Bold, Italic, Strikethrough, Heading1, Heading2,
  Heading3, Copy, Check, Sparkles, Layers, History, Shield, CornerDownLeft, ClipboardPaste,
  Wand2, LayoutGrid, FileCode, Image as ImageIcon, FileDown
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/meta";

const PAGE_STATUSES = [
  { id: "DRAFT", label: "Draft", cls: "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20" },
  { id: "IN REVIEW", label: "In Review", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  { id: "TECHNICAL REVIEW", label: "Technical Review", cls: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20" },
  { id: "APPROVED", label: "Approved", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  { id: "PUBLISHED", label: "Published", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" },
  { id: "DEPRECATED", label: "Deprecated", cls: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20" },
];

const EMOJIS = ["👍", "❤️", "🎉", "🚀", "💡", "🔥", "👀", "🙌"];

function dataURLtoFile(dataurl, filename) {
  try {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  } catch {
    return null;
  }
}

/**
 * Converts HTML string (e.g. copied from Confluence / Word / Web) into clean Markdown tables, images & formatted blocks.
 */
function htmlToMarkdown(htmlString) {
  if (!htmlString) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Remove non-content style, script, xml, head, meta, link tags
    doc.querySelectorAll("style, script, xml, head, meta, link, title").forEach(el => el.remove());

    function parseNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        const parentTag = node.parentNode?.tagName?.toLowerCase();
        if (["style", "script", "xml", "head", "meta", "link"].includes(parentTag)) return "";
        return node.textContent;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return "";

      const tag = node.tagName.toLowerCase();
      if (["style", "script", "xml", "head", "meta", "link", "title"].includes(tag)) return "";

      const children = Array.from(node.childNodes).map(parseNode).join("");

      switch (tag) {
        case "h1": return `\n\n# ${children.trim()}\n\n`;
        case "h2": return `\n\n## ${children.trim()}\n\n`;
        case "h3": return `\n\n### ${children.trim()}\n\n`;
        case "h4": return `\n\n#### ${children.trim()}\n\n`;
        case "p": return `\n\n${children.trim()}\n\n`;
        case "strong":
        case "b": return `**${children.trim()}**`;
        case "em":
        case "i": return `*${children.trim()}*`;
        case "code": return `\`${children.trim()}\``;
        case "pre": return `\n\n\`\`\`\n${children.trim()}\n\`\`\`\n\n`;
        case "blockquote": return `\n\n> ${children.trim()}\n\n`;
        case "ul":
        case "ol": return `\n\n${children}\n\n`;
        case "li": return `- ${children.trim()}\n`;
        case "hr": return `\n\n---\n\n`;
        case "a": {
          const href = node.getAttribute("href") || "#";
          return `[${children.trim()}](${href})`;
        }
        case "img": {
          let src = node.getAttribute("src") || node.getAttribute("data-image-src") || node.getAttribute("data-src") || "";
          const alt = node.getAttribute("alt") || node.getAttribute("data-media-id") || "Page Image";
          if (!src) return "";
          const token = localStorage.getItem("ft_token") || "";
          if (src.startsWith("/api/attachments/") && token && !src.includes("auth=")) {
            src += (src.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(token);
          }
          return `\n\n![${alt}](${src})\n\n`;
        }
        case "table": {
          const rows = Array.from(node.querySelectorAll("tr"));
          if (!rows.length) return "";
          let mdTable = "\n\n";

          rows.forEach((row, rowIndex) => {
            const cells = Array.from(row.querySelectorAll("th, td"));
            const cellTexts = cells.map(c => parseNode(c).replace(/\s+/g, " ").trim().replace(/\|/g, "\\|"));
            mdTable += `| ${cellTexts.join(" | ")} |\n`;

            if (rowIndex === 0) {
              const separators = Array(cellTexts.length).fill("---");
              mdTable += `| ${separators.join(" | ")} |\n`;
            }
          });
          return mdTable + "\n\n";
        }
        default:
          return children;
      }
    }

    let result = parseNode(doc.body);
    result = result
      .replace(/\{[^\}]*mso-[^\}]*\}/gi, "")
      .replace(/mso-[^;]+;/gi, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    return result;
  } catch (err) {
    console.error("Failed to parse HTML to markdown:", err);
    return "";
  }
}

function IssueKeyLink({ k }) {
  const app = useApp();
  const onClick = async (e) => {
    e.preventDefault();
    try {
      const r = await api.get(`/issues/${k}`);
      if (r?.data?.id) app.openIssue(r.data.id);
    } catch {}
  };
  return (
    <a
      href="#"
      onClick={onClick}
      className="inline-flex items-center font-mono text-xs bg-primary/10 text-primary font-medium rounded px-1.5 py-0.5 hover:bg-primary/20 no-underline transition-colors mx-0.5"
      data-testid={`issuekey-link-${k}`}
    >
      {k}
    </a>
  );
}

function CodeBlock({ children, className }) {
  const [copied, setCopied] = useState(false);
  const code = String(children || "").replace(/\n$/, "");
  const match = /language-(\w+)/.exec(className || "");
  const lang = match ? match[1] : "";

  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-4 rounded-md overflow-hidden border border-border bg-muted/40 font-mono text-xs">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/80 border-b border-border text-[11px] text-muted-foreground select-none">
        <span>{lang || "code"}</span>
        <button onClick={copy} className="flex items-center gap-1 hover:text-foreground">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
      <pre className="p-3 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function CalloutBox({ type, children }) {
  const configs = {
    info: { icon: Info, cls: "bg-blue-500/10 border-blue-500/30 text-blue-900 dark:text-blue-200", title: "INFO" },
    warning: { icon: AlertCircle, cls: "bg-amber-500/10 border-amber-500/30 text-amber-900 dark:text-amber-200", title: "WARNING" },
    success: { icon: CheckCircle2, cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-900 dark:text-emerald-200", title: "SUCCESS" },
    note: { icon: Sparkles, cls: "bg-purple-500/10 border-purple-500/30 text-purple-900 dark:text-purple-200", title: "NOTE" },
    danger: { icon: Trash2, cls: "bg-red-500/10 border-red-500/30 text-red-900 dark:text-red-200", title: "DANGER" },
  };
  const cfg = configs[type] || configs.info;
  const Icon = cfg.icon;

  return (
    <div className={cn("p-3.5 my-4 rounded-lg border flex gap-3 text-sm shadow-sm", cfg.cls)}>
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div className="flex-1 space-y-1">
        <div className="font-semibold text-xs uppercase tracking-wider">{cfg.title}</div>
        <div>{children}</div>
      </div>
    </div>
  );
}

function TableOfContents({ content }) {
  const lines = (content || "").split("\n");
  const headings = [];
  lines.forEach((line) => {
    const match = /^(#{1,3})\s+(.+)$/.exec(line);
    if (match) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
      headings.push({ level, text, id });
    }
  });

  if (!headings.length) return null;

  return (
    <div className="my-5 p-3.5 border rounded-lg bg-muted/20 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
        <List className="w-3.5 h-3.5" /> Table of Contents
      </div>
      <div className="space-y-1">
        {headings.map((h, i) => (
          <a
            key={i}
            href={`#${h.id}`}
            style={{ paddingLeft: (h.level - 1) * 12 }}
            className="block text-xs text-muted-foreground hover:text-primary transition-colors py-0.5 truncate"
          >
            {h.text}
          </a>
        ))}
      </div>
    </div>
  );
}

const customRenderer = (content) => ({
  h1: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h1 id={id} className="font-display text-2xl font-semibold tracking-tight mt-6 mb-3 border-b pb-1" {...props} />;
  },
  h2: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h2 id={id} className="font-display text-xl font-semibold tracking-tight mt-5 mb-2" {...props} />;
  },
  h3: (props) => {
    const text = String(props.children || "");
    const id = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
    return <h3 id={id} className="font-display text-lg font-medium tracking-tight mt-4 mb-2" {...props} />;
  },
  p: (props) => {
    const text = String(props.children || "");
    if (text.startsWith(":::info")) return <CalloutBox type="info">{text.replace(/^:::info\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::warning")) return <CalloutBox type="warning">{text.replace(/^:::warning\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::success")) return <CalloutBox type="success">{text.replace(/^:::success\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::note")) return <CalloutBox type="note">{text.replace(/^:::note\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text.startsWith(":::danger")) return <CalloutBox type="danger">{text.replace(/^:::danger\s*/, "").replace(/:::$/, "")}</CalloutBox>;
    if (text === "[TOC]") return <TableOfContents content={content} />;
    return <p className="text-sm leading-relaxed my-3" {...props} />;
  },
  code: ({ inline, className, children, ...rest }) => {
    if (inline) {
      return <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded border border-border" {...rest}>{children}</code>;
    }
    return <CodeBlock className={className}>{children}</CodeBlock>;
  },
  img: (props) => {
    const token = localStorage.getItem("ft_token") || "";
    let src = props.src || "";
    if (src.startsWith("/api/attachments/") && token && !src.includes("auth=")) {
      src += (src.includes("?") ? "&" : "?") + "auth=" + encodeURIComponent(token);
    }
    return (
      <span className="block my-4">
        <img
          src={src}
          alt={props.alt || "Page Image"}
          className="max-w-full max-h-[600px] rounded-lg border border-border shadow-md object-contain transition-transform hover:scale-[1.01]"
        />
        {props.alt && props.alt !== "Page Image" && (
          <span className="block text-center text-xs text-muted-foreground mt-1.5 font-medium">{props.alt}</span>
        )}
      </span>
    );
  },
  table: (props) => (
    <div className="overflow-x-auto my-4 border border-border rounded-lg shadow-sm">
      <table className="w-full text-sm border-collapse bg-background" {...props} />
    </div>
  ),
  th: (props) => <th className="text-left px-3.5 py-2.5 bg-muted/60 border-b border-border font-semibold text-xs uppercase tracking-wider text-muted-foreground" {...props} />,
  td: (props) => <td className="px-3.5 py-2.5 border-b border-border/50 text-sm" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-primary/60 pl-4 py-1 text-muted-foreground italic my-4" {...props} />,
  text: (props) => {
    const value = String(props.children || "");
    const re = /([A-Z]{2,5}-\d+)/g;
    const parts = [];
    let last = 0; let m; let key = 0;
    while ((m = re.exec(value)) !== null) {
      if (m.index > last) parts.push(value.slice(last, m.index));
      parts.push(<IssueKeyLink key={key++} k={m[1]} />);
      last = m.index + m[0].length;
    }
    if (last < value.length) parts.push(value.slice(last));
    return parts.length ? <>{parts}</> : value;
  },
});

function flattenTree(pages, expanded, isSearchMode) {
  const byParent = {};
  pages.forEach((p) => {
    const k = p.parent_id || "_root";
    (byParent[k] = byParent[k] || []).push(p);
  });
  const out = [];
  const walk = (parentId, depth) => {
    const kids = byParent[parentId || "_root"] || [];
    kids.forEach((p) => {
      const childList = byParent[p.id] || [];
      const hasChildren = childList.length > 0;
      const isOpen = isSearchMode || expanded.has(p.id);
      out.push({ page: p, depth, hasChildren, isOpen });
      if (isOpen && hasChildren) walk(p.id, depth + 1);
    });
  };
  walk(null, 0);
  return out;
}

export default function Pages(props) {
  const scopedProjectId = props.scopedProjectId;
  const { projects } = useApp();
  const [projectFilter, setProjectFilter] = useState(scopedProjectId || "global");
  const [tree, setTree] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [q, setQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => { setProjectFilter(scopedProjectId || "global"); setSelectedId(null); }, [scopedProjectId]);

  const load = useCallback(async () => {
    const params = projectFilter === "global" ? { project_id: "global" } : { project_id: projectFilter };
    const r = await api.get("/pages", { params });
    setTree(r.data);
    if (!selectedId && r.data.length) {
      const root = r.data.find((p) => !p.parent_id);
      setSelectedId(root ? root.id : r.data[0].id);
    }
  }, [projectFilter, selectedId]);

  useEffect(() => { load(); }, [load]);

  useRealtime((ev) => {
    if (ev.type === "page.updated") load();
  }, [load]);

  useEffect(() => {
    if (!q) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      const params = projectFilter === "global" ? { q, project_id: "global" } : { q, project_id: projectFilter };
      const r = await api.get("/pages", { params });
      setSearchResults(r.data);
    }, 200);
    return () => clearTimeout(t);
  }, [q, projectFilter]);

  const createPage = async (parent_id) => {
    const payload = {
      title: "Untitled",
      content: "# Untitled Document\n\nStart typing, paste images/tables from Confluence...\n\n| Feature | Status | Priority |\n| --- | --- | --- |\n| Confluence Image Import | Active | High |\n\n:::info\nUse `/` commands or formatting toolbar to insert images, panels, tables, or code blocks.\n:::\n",
      project_id: projectFilter === "global" ? null : projectFilter,
      parent_id: parent_id || null,
      status: "DRAFT",
    };
    const r = await api.post("/pages", payload);
    setSelectedId(r.data.id);
    if (parent_id) {
      const next = new Set(expanded);
      next.add(parent_id);
      setExpanded(next);
    }
    load();
  };

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const flat = useMemo(() => {
    const source = searchResults || tree;
    return flattenTree(source, expanded, !!searchResults);
  }, [tree, expanded, searchResults]);

  return (
    <div className="grid h-full grid-cols-[280px_1fr]">
      <aside className="border-r border-border overflow-y-auto bg-secondary/30">
        <div className="p-3 border-b border-border space-y-2">
          {!scopedProjectId && (
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="h-8" data-testid="pages-project-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">Global Space</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages..." className="h-8 pl-7" data-testid="pages-search" />
          </div>
          <Button size="sm" onClick={() => createPage(null)} className="w-full gap-1.5" data-testid="new-page-btn">
            <Plus className="w-4 h-4" /> New page
          </Button>
        </div>
        <div className="p-2 space-y-0.5">
          {flat.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-6">No pages yet</div>
          )}
          {flat.map((node) => (
            <PageRow
              key={node.page.id}
              node={node}
              selected={selectedId}
              onSelect={setSelectedId}
              onToggle={toggleExpand}
              onAddChild={createPage}
            />
          ))}
        </div>
      </aside>
      <main className="overflow-y-auto bg-background">
        {selectedId ? (
          <PageEditor pageId={selectedId} onDeleted={() => { setSelectedId(null); load(); }} onSaved={load} />
        ) : (
          <div className="h-full grid place-items-center">
            <div className="text-center p-6">
              <BookOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <div className="font-display text-xl font-semibold">Confluence Workspace</div>
              <div className="text-sm text-muted-foreground mt-1 mb-4">Select a document from the sidebar or create a new page.</div>
              <Button onClick={() => createPage(null)}>
                <Plus className="w-4 h-4 mr-1.5" /> Create page
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function PageRow(props) {
  const { node, selected, onSelect, onToggle, onAddChild } = props;
  const { page, depth, hasChildren, isOpen } = node;
  const rowClass = cn(
    "flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm cursor-pointer group transition-colors select-none",
    selected === page.id
      ? "bg-accent text-foreground font-medium"
      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
  );
  return (
    <div
      onClick={() => onSelect(page.id)}
      data-testid={`page-node-${page.id}`}
      className={rowClass}
      style={{ paddingLeft: 8 + depth * 14 }}
    >
      {hasChildren ? (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(page.id); }}
          className="text-muted-foreground shrink-0 hover:text-foreground"
        >
          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
      ) : (
        <span className="w-3.5" />
      )}
      <FileText className="w-3.5 h-3.5 shrink-0 text-primary/80" />
      <span className="truncate flex-1">{page.title || "Untitled"}</span>
      <button
        onClick={(e) => { e.stopPropagation(); onAddChild(page.id); }}
        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition-opacity"
        data-testid={`add-child-${page.id}`}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function PageEditor({ pageId, onDeleted, onSaved }) {
  const { users } = useApp();
  const [page, setPage] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [pinned, setPinned] = useState(false);
  const [reactions, setReactions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState("visual");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [slashOpen, setSlashOpen] = useState(false);
  const textareaRef = useRef(null);
  const imageInputRef = useRef(null);

  const author = useMemo(() => {
    return users.find((u) => u.id === page?.author_id) || { name: "Yuvraj Choudhary" };
  }, [users, page?.author_id]);

  const readingTime = useMemo(() => {
    const words = (content || "").trim().split(/\s+/).filter(Boolean).length;
    const mins = Math.ceil(words / 200);
    return `${mins} min read`;
  }, [content]);

  useEffect(() => {
    api.get(`/pages/${pageId}`).then((r) => {
      setPage(r.data);
      setTitle(r.data.title || "Untitled");
      setContent(r.data.content || "");
      setStatus(r.data.status || "DRAFT");
      setPinned(r.data.pinned || false);
      setReactions(r.data.reactions || {});
      setDirty(false);
    });
  }, [pageId]);

  const save = async () => {
    const r = await api.patch(`/pages/${pageId}`, { title, content, status, pinned, reactions });
    setPage(r.data);
    toast.success("Page published & saved");
    setDirty(false);
    onSaved?.();
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (dirty) save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const del = async () => {
    if (!window.confirm("Delete this page and all sub-pages?")) return;
    await api.delete(`/pages/${pageId}`);
    toast.success("Page deleted");
    onDeleted?.();
  };

  const toggleListen = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    } else {
      const text = `${title}. ${content.replace(/[\#\*\_`\:\-\>\<\=\+]/g, " ")}`;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utterance);
      setSpeaking(true);
    }
  };

  const toggleReaction = async (emoji) => {
    const curUser = JSON.parse(localStorage.getItem("ft_user") || "{}");
    const uid = curUser.id || "me";
    const next = { ...reactions };
    if (next[uid] === emoji) {
      delete next[uid];
    } else {
      next[uid] = emoji;
    }
    setReactions(next);
    setDirty(true);
    await api.patch(`/pages/${pageId}`, { reactions: next });
  };

  const insertSnippet = (snippet) => {
    if (!textareaRef.current) {
      setContent((prev) => prev + "\n\n" + snippet);
      setDirty(true);
      return;
    }
    const ta = textareaRef.current;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const nextContent = content.substring(0, start) + snippet + content.substring(end);
    setContent(nextContent);
    setDirty(true);
    setSlashOpen(false);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + snippet.length;
    }, 50);
  };

  const uploadAndInsertImage = async (file) => {
    try {
      toast.info("Uploading image...");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("page_id", pageId);
      const r = await api.post("/attachments/upload", fd);
      const token = localStorage.getItem("ft_token") || "";
      const imgUrl = `/api/attachments/${r.data.id}/download?auth=${encodeURIComponent(token)}`;
      const snippet = `\n![${r.data.original_filename || "Page Image"}](${imgUrl})\n`;
      insertSnippet(snippet);
      toast.success("Image uploaded & inserted into page!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload image");
    }
  };

  const processPastedContent = async (mdText) => {
    if (!mdText) return;
    const base64Regex = /!\[([^\]]*)\]\((data:image\/[a-zA-Z]+;base64,[^\)]+)\)/g;
    let match;
    let nextContent = mdText;
    let uploadedCount = 0;

    const matches = [];
    while ((match = base64Regex.exec(mdText)) !== null) {
      matches.push({ full: match[0], alt: match[1], dataUrl: match[2] });
    }

    if (matches.length > 0) {
      toast.info(`Uploading ${matches.length} pasted image(s)...`);
      for (const m of matches) {
        const file = dataURLtoFile(m.dataUrl, `${(m.alt || "pasted_image").replace(/\s+/g, "_")}.png`);
        if (file) {
          try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("page_id", pageId);
            const r = await api.post("/attachments/upload", fd);
            const token = localStorage.getItem("ft_token") || "";
            const imgUrl = `/api/attachments/${r.data.id}/download?auth=${encodeURIComponent(token)}`;
            nextContent = nextContent.replace(m.dataUrl, imgUrl);
            uploadedCount++;
          } catch (e) {
            console.error("Failed to upload base64 image", e);
          }
        }
      }
    }

    insertSnippet(nextContent);
    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} pasted image(s) & inserted into document!`);
    } else {
      toast.success("Pasted and converted Confluence content!");
    }
  };

  /**
   * Intercepts HTML / Confluence clipboard pastes and image files!
   */
  const handlePaste = async (e) => {
    // 1. Image Files in Clipboard (e.g. screenshot or copied file)
    if (e.clipboardData?.items) {
      for (let i = 0; i < e.clipboardData.items.length; i++) {
        const item = e.clipboardData.items[i];
        if (item.type && item.type.startsWith("image/")) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await uploadAndInsertImage(file);
            return;
          }
        }
      }
    }

    // 2. HTML / Confluence Content (tables, headings, images)
    const html = e.clipboardData?.getData("text/html");
    if (html && (html.includes("<table") || html.includes("<img") || html.includes("<h") || html.includes("<ul") || html.includes("<ol") || html.includes("<p>"))) {
      e.preventDefault();
      const md = htmlToMarkdown(html);
      if (md) {
        await processPastedContent(md);
      }
    }
  };

  const handleDrop = (e) => {
    if (e.dataTransfer?.files?.length) {
      const file = e.dataTransfer.files[0];
      if (file.type && file.type.startsWith("image/")) {
        e.preventDefault();
        uploadAndInsertImage(file);
      }
    }
  };

  const handleTextareaChange = (e) => {
    const val = e.target.value;
    setContent(val);
    setDirty(true);
    const pos = e.target.selectionStart;
    const lastChar = val.substring(pos - 1, pos);
    if (lastChar === "/") {
      setSlashOpen(true);
    } else if (slashOpen && (lastChar === " " || lastChar === "\n")) {
      setSlashOpen(false);
    }
  };

  const pasteConfluenceClipboard = async () => {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes("text/html")) {
          const blob = await item.getType("text/html");
          const htmlText = await blob.text();
          const md = htmlToMarkdown(htmlText);
          if (md) {
            await processPastedContent(md);
            return;
          }
        } else if (item.types.some(t => t.startsWith("image/"))) {
          const type = item.types.find(t => t.startsWith("image/"));
          const blob = await item.getType(type);
          const file = new File([blob], "pasted_image.png", { type });
          await uploadAndInsertImage(file);
          return;
        }
      }
      const plainText = await navigator.clipboard.readText();
      if (plainText) {
        insertSnippet(plainText);
        toast.success("Pasted clipboard text");
      }
    } catch {
      toast.info("Press Ctrl+V (or Cmd+V) to paste directly into the editor!");
    }
  };

  const exportToPdf = () => {
    try {
      const contentEl = document.getElementById("pdf-document-printable");
      const contentHtml = contentEl ? contentEl.innerHTML : "";

      const printFrame = document.createElement("iframe");
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "none";
      printFrame.style.zIndex = "-1000";
      document.body.appendChild(printFrame);

      const doc = printFrame.contentWindow.document;

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>${title || "Document"}</title>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap');
              * { box-sizing: border-box; }
              body { font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif; padding: 24px; color: #0f172a; line-height: 1.6; background: #ffffff; margin: 0; }
              h1.pdf-title { font-size: 28px; font-weight: 700; color: #0f172a; margin-top: 0; margin-bottom: 8px; border-bottom: 2px solid #2563eb; padding-bottom: 8px; }
              .pdf-meta { font-size: 12px; color: #64748b; margin-bottom: 24px; display: flex; gap: 12px; align-items: center; }
              .pdf-meta span { display: inline-block; }
              table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 13px; }
              th, td { border: 1px solid #cbd5e1; padding: 8px 12px; text-align: left; }
              th { background: #f1f5f9; font-weight: 600; color: #334155; }
              pre { background: #0f172a; color: #f8fafc; padding: 14px; border-radius: 6px; overflow-x: auto; font-family: monospace; font-size: 12px; }
              code { background: #f1f5f9; color: #0f172a; padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 12px; }
              img { max-width: 100%; max-height: 500px; border-radius: 6px; border: 1px solid #e2e8f0; margin: 14px 0; display: block; object-fit: contain; }
              blockquote { border-left: 4px solid #3b82f6; padding-left: 14px; margin: 14px 0; color: #475569; font-style: italic; }
              @page { size: A4; margin: 1.2cm; }
            </style>
          </head>
          <body>
            <h1 class="pdf-title">${title || "Untitled Page"}</h1>
            <div class="pdf-meta">
              <span><strong>Author:</strong> ${author.name}</span>
              <span>|</span>
              <span><strong>Status:</strong> ${status}</span>
              <span>|</span>
              <span><strong>Est. Reading Time:</strong> ${readingTime}</span>
            </div>
            <div class="pdf-body">
              ${contentHtml}
            </div>
          </body>
        </html>
      `;

      doc.open();
      doc.write(htmlContent);
      doc.close();

      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => {
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        }, 1000);
      }, 300);
    } catch (err) {
      console.error("PDF Export error:", err);
      toast.error("Failed to generate PDF export");
    }
  };

  if (!page) return <div className="p-8 text-sm text-muted-foreground">Loading page...</div>;

  const statusMeta = PAGE_STATUSES.find((s) => s.id === status) || PAGE_STATUSES[0];
  const reactionCounts = Object.values(reactions).reduce((acc, emo) => {
    acc[emo] = (acc[emo] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6" onPaste={handlePaste} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
      {/* Hidden file input for image uploads */}
      <input
        type="file"
        ref={imageInputRef}
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) {
            uploadAndInsertImage(e.target.files[0]);
            e.target.value = "";
          }
        }}
      />

      {/* Confluence Top Navigation & Actions Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-border flex-wrap gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap">
          {(page.breadcrumb || []).map((b, i) => (
            <span key={b.id} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5" />}
              <span>{b.title}</span>
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2">
          {/* Mode Switcher Buttons */}
          <div className="flex border border-border rounded-md overflow-hidden bg-muted/30 p-0.5 gap-0.5">
            <button
              onClick={() => setMode("visual")}
              className={cn("px-2.5 py-1 text-xs font-medium flex items-center gap-1.5 rounded transition-colors", mode === "visual" ? "bg-primary text-primary-foreground shadow-sm" : "hover:bg-accent")}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Visual Editor
            </button>
            <button
              onClick={() => setMode("markdown")}
              className={cn("px-2.5 py-1 text-xs font-medium flex items-center gap-1.5 rounded transition-colors", mode === "markdown" ? "bg-primary text-primary-foreground shadow-sm" : "hover:bg-accent")}
            >
              <FileCode className="w-3.5 h-3.5" /> Markdown
            </button>
            <button
              onClick={() => setMode("split")}
              className={cn("px-2.5 py-1 text-xs font-medium flex items-center gap-1.5 rounded transition-colors", mode === "split" ? "bg-primary text-primary-foreground shadow-sm" : "hover:bg-accent")}
            >
              Split
            </button>
            <button
              onClick={() => setMode("preview")}
              className={cn("px-2.5 py-1 text-xs font-medium flex items-center gap-1.5 rounded transition-colors", mode === "preview" ? "bg-primary text-primary-foreground shadow-sm" : "hover:bg-accent")}
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
          </div>

          <Button size="sm" onClick={pasteConfluenceClipboard} variant="outline" className="gap-1 text-xs" title="Paste Confluence HTML / Table">
            <ClipboardPaste className="w-3.5 h-3.5 text-primary" /> Paste Confluence
          </Button>

          <Button size="sm" onClick={exportToPdf} variant="outline" className="gap-1 text-xs" title="Export page to PDF">
            <FileDown className="w-3.5 h-3.5 text-emerald-500" /> Export PDF
          </Button>

          <Button size="sm" onClick={() => setHistoryOpen(true)} variant="outline" className="gap-1 text-xs">
            <History className="w-3.5 h-3.5" /> History
          </Button>

          <Button size="sm" onClick={() => { setPinned(!pinned); setDirty(true); }} variant={pinned ? "secondary" : "outline"} className="gap-1 text-xs">
            <Pin className="w-3.5 h-3.5" /> {pinned ? "Pinned" : "Pin"}
          </Button>

          <Button size="sm" onClick={save} disabled={!dirty} data-testid="page-save" className="gap-1.5">
            <Save className="w-3.5 h-3.5" /> Save & Update
          </Button>

          <Button size="icon" variant="ghost" onClick={del} className="h-8 w-8 text-muted-foreground hover:text-red-500">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Confluence Rich Metadata Header Bar */}
      <div className="space-y-4">
        {/* Title Input */}
        <input
          value={title}
          onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
          placeholder="Untitled Page"
          className="font-display text-3xl lg:text-4xl font-bold tracking-tight bg-transparent border-none outline-none w-full hover:bg-accent/20 rounded px-1 -mx-1 transition-colors"
          data-testid="page-title-input"
        />

        {/* Metadata Strip */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap pt-1 border-b pb-4">
          <div className="flex items-center gap-1.5 font-medium text-foreground">
            <Avatar className="w-5 h-5 border">
              <AvatarImage src={author.avatar_url} />
              <AvatarFallback className="text-[9px]">{initials(author.name)}</AvatarFallback>
            </Avatar>
            <span>By {author.name}</span>
          </div>

          <div className="h-3 w-px bg-border" />

          {/* Status Badge Dropdown */}
          <Select value={status} onValueChange={(v) => { setStatus(v); setDirty(true); }}>
            <SelectTrigger className={cn("h-6 text-[11px] font-semibold px-2 border rounded-full uppercase tracking-wider", statusMeta.cls)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_STATUSES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  <span className={cn("inline-block px-2 py-0.5 rounded text-[10px] font-mono font-semibold uppercase", s.cls)}>{s.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="h-3 w-px bg-border" />

          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            <span>{readingTime}</span>
          </div>

          <div className="h-3 w-px bg-border" />

          {/* Speech synthesis Listen Button */}
          <button
            type="button"
            onClick={toggleListen}
            className={cn("flex items-center gap-1 px-2 py-0.5 rounded-full border transition-colors", speaking ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}
          >
            {speaking ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            <span>{speaking ? "Stop listening" : "Listen"}</span>
          </button>

          <div className="h-3 w-px bg-border" />

          {/* Reactions bar */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {Object.entries(reactionCounts).map(([emo, count]) => (
              <button
                key={emo}
                onClick={() => toggleReaction(emo)}
                className="flex items-center gap-1 px-2 py-0.5 rounded-full border bg-muted/40 hover:bg-muted text-xs transition-colors"
              >
                <span>{emo}</span>
                <span className="font-mono text-[10px] font-semibold">{count}</span>
              </button>
            ))}

            <div className="relative group">
              <button type="button" className="flex items-center gap-1 px-2 py-0.5 rounded-full border hover:bg-accent text-xs">
                <Smile className="w-3.5 h-3.5" />
                <span>React</span>
              </button>
              <div className="absolute left-0 top-full mt-1 hidden group-hover:flex items-center gap-1 p-1.5 bg-background border rounded-lg shadow-lg z-20">
                {EMOJIS.map((emo) => (
                  <button key={emo} onClick={() => toggleReaction(emo)} className="p-1 hover:bg-accent rounded text-base transition-transform hover:scale-125">
                    {emo}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Formatting Toolbar */}
      {mode !== "preview" && (
        <div className="sticky top-0 z-10 flex items-center gap-1 p-1.5 bg-background/95 backdrop-blur border rounded-lg shadow-sm flex-wrap">
          <Button type="button" size="xs" variant="ghost" title="Heading 1" onClick={() => insertSnippet("\n# Heading 1\n")}><Heading1 className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Heading 2" onClick={() => insertSnippet("\n## Heading 2\n")}><Heading2 className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Heading 3" onClick={() => insertSnippet("\n### Heading 3\n")}><Heading3 className="w-4 h-4" /></Button>
          <div className="h-4 w-px bg-border mx-1" />
          <Button type="button" size="xs" variant="ghost" title="Bold" onClick={() => insertSnippet("**bold**")}><Bold className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Italic" onClick={() => insertSnippet("*italic*")}><Italic className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Strikethrough" onClick={() => insertSnippet("~~strikethrough~~")}><Strikethrough className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Inline Code" onClick={() => insertSnippet("`code`")}><Code className="w-4 h-4" /></Button>
          <div className="h-4 w-px bg-border mx-1" />
          <Button type="button" size="xs" variant="ghost" title="Bullet List" onClick={() => insertSnippet("\n- Item 1\n- Item 2\n")}><List className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Numbered List" onClick={() => insertSnippet("\n1. First step\n2. Second step\n")}><ListOrdered className="w-4 h-4" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Task List" onClick={() => insertSnippet("\n- [ ] Task 1\n- [ ] Task 2\n")}><CheckSquare className="w-4 h-4" /></Button>
          <div className="h-4 w-px bg-border mx-1" />
          <Button type="button" size="xs" variant="ghost" title="Upload / Insert Image" onClick={() => imageInputRef.current?.click()}>
            <ImageIcon className="w-4 h-4 text-emerald-500" />
          </Button>
          <Button type="button" size="xs" variant="ghost" title="Info Panel" onClick={() => insertSnippet("\n:::info\nThis is an info callout panel.\n:::\n")}><Info className="w-4 h-4 text-blue-500" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Warning Panel" onClick={() => insertSnippet("\n:::warning\nThis is a warning alert panel.\n:::\n")}><AlertCircle className="w-4 h-4 text-amber-500" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Success Panel" onClick={() => insertSnippet("\n:::success\nOperation completed successfully.\n:::\n")}><CheckCircle2 className="w-4 h-4 text-emerald-500" /></Button>
          <div className="h-4 w-px bg-border mx-1" />
          <Button type="button" size="xs" variant="ghost" title="Code Block" onClick={() => insertSnippet("\n```javascript\n// Write code here\n```\n")}><Code className="w-4 h-4 text-purple-500" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Insert Table" onClick={() => insertSnippet("\n| Header 1 | Header 2 |\n| --- | --- |\n| Cell 1 | Cell 2 |\n")}><Table className="w-4 h-4 text-primary" /></Button>
          <Button type="button" size="xs" variant="ghost" title="Table of Contents" onClick={() => insertSnippet("\n[TOC]\n")}><List className="w-4 h-4 text-primary" /></Button>
        </div>
      )}

      {/* Slash Commands Dropdown Menu */}
      {slashOpen && (
        <div className="p-2 border rounded-lg bg-popover shadow-xl space-y-1 text-xs z-30 max-w-xs border-primary/30">
          <div className="font-semibold text-muted-foreground px-2 py-1 border-b uppercase tracking-wider text-[10px]">Confluence Block Inserts</div>
          <button onClick={() => insertSnippet("# Heading 1\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
            <Heading1 className="w-4 h-4" /> Heading 1
          </button>
          <button onClick={() => insertSnippet("## Heading 2\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
            <Heading2 className="w-4 h-4" /> Heading 2
          </button>
          <button onClick={() => imageInputRef.current?.click()} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-emerald-500">
            <ImageIcon className="w-4 h-4" /> Upload Image
          </button>
          <button onClick={() => insertSnippet(":::info\nInfo notice text...\n:::\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-blue-500">
            <Info className="w-4 h-4" /> Info Panel
          </button>
          <button onClick={() => insertSnippet(":::warning\nWarning notice text...\n:::\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-amber-500">
            <AlertCircle className="w-4 h-4" /> Warning Panel
          </button>
          <button onClick={() => insertSnippet(":::success\nSuccess notice text...\n:::\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2 text-emerald-500">
            <CheckCircle2 className="w-4 h-4" /> Success Panel
          </button>
          <button onClick={() => insertSnippet("```javascript\n// Code block\n```\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
            <Code className="w-4 h-4" /> Code Block
          </button>
          <button onClick={() => insertSnippet("| Header 1 | Header 2 |\n| --- | --- |\n| Cell 1 | Cell 2 |\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
            <Table className="w-4 h-4" /> Insert Table
          </button>
          <button onClick={() => insertSnippet("[TOC]\n")} className="w-full text-left px-2 py-1.5 hover:bg-accent rounded flex items-center gap-2">
            <List className="w-4 h-4" /> Table of Contents
          </button>
        </div>
      )}

      {/* Editor & Preview Workspace */}
      <div className="space-y-4">
        {mode === "visual" && (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground font-medium flex items-center justify-between border-b pb-1">
              <span className="flex items-center gap-1.5"><LayoutGrid className="w-3.5 h-3.5 text-primary" /> Visual Document Canvas (WYSIWYG Mode)</span>
              <span className="text-[11px]">Paste Confluence images/tables or drag & drop image files into the canvas</span>
            </div>
            
            {/* Visual Live Canvas showing formatted document + quick edit textarea */}
            <div className="border rounded-xl p-6 bg-background shadow-sm space-y-6">
              <div id="pdf-document-printable" className="prose prose-sm dark:prose-invert max-w-none border-b pb-6">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={customRenderer(content)}>
                  {content}
                </ReactMarkdown>
              </div>

              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Document Source & Quick Edit</div>
                <Textarea
                  ref={textareaRef}
                  value={content}
                  onChange={handleTextareaChange}
                  onPaste={handlePaste}
                  rows={10}
                  placeholder="Type or paste images, tables & content from Confluence..."
                  className="font-mono text-xs resize-y min-h-[160px] p-3 border rounded-lg bg-muted/20"
                />
              </div>
            </div>
          </div>
        )}

        {mode === "markdown" && (
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground font-medium">Markdown Source Mode (Type / for block inserts, drag & drop or paste images)</div>
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={handleTextareaChange}
              onPaste={handlePaste}
              rows={24}
              placeholder="Write document using Markdown, or paste images/tables from Confluence..."
              className="font-mono text-sm resize-none min-h-[550px] leading-relaxed p-4 border rounded-lg shadow-inner bg-muted/10"
              data-testid="page-content-textarea"
            />
          </div>
        )}

        {mode === "split" && (
          <div className="grid grid-cols-2 gap-6 min-h-[550px]">
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={handleTextareaChange}
              onPaste={handlePaste}
              rows={24}
              placeholder="Write Markdown or paste Confluence content & images..."
              className="font-mono text-sm resize-none min-h-[550px] leading-relaxed p-4 border rounded-lg shadow-inner bg-muted/10"
            />
            <div className="prose prose-sm dark:prose-invert max-w-none border rounded-lg p-6 bg-background shadow-sm overflow-auto min-h-[550px]">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={customRenderer(content)}>
                {content}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {mode === "preview" && (
          <div className="prose prose-sm dark:prose-invert max-w-none border rounded-lg p-6 bg-background shadow-sm overflow-auto min-h-[550px]" data-testid="page-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={customRenderer(content)}>
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Page Comments Section */}
      <PageComments pageId={pageId} />

      {/* Version History Modal */}
      <VersionHistoryModal open={historyOpen} setOpen={setHistoryOpen} history={page.history || []} onRestore={(h) => { setTitle(h.title); setContent(h.content); setDirty(true); setHistoryOpen(false); toast.success(`Restored to version ${h.version}`); }} />
    </div>
  );
}

function VersionHistoryModal({ open, setOpen, history, onRestore }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <History className="w-5 h-5 text-primary" /> Document Version History
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">No previous versions saved yet.</div>
          ) : (
            history.slice().reverse().map((h, i) => (
              <div key={i} className="p-3 border rounded-lg bg-muted/20 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-primary">Version #{h.version}</span>
                  <span className="text-muted-foreground">{new Date(h.updated_at).toLocaleString()}</span>
                </div>
                <div className="font-medium text-sm">{h.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2 font-mono bg-background p-2 rounded border">
                  {h.content}
                </div>
                <div className="flex justify-end pt-1">
                  <Button size="xs" variant="outline" onClick={() => onRestore(h)} className="gap-1">
                    <CornerDownLeft className="w-3 h-3" /> Restore this version
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PageComments({ pageId }) {
  const { users } = useApp();
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      const r = await api.get(`/pages/${pageId}/comments`);
      setComments(r.data || []);
    } catch {}
  }, [pageId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const submitComment = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/pages/${pageId}/comments`, { body });
      setBody("");
      toast.success("Comment added");
      loadComments();
    } catch {
      toast.error("Failed to add comment");
    } finally {
      setSubmitting(false);
    }
  };

  const delComment = async (cid) => {
    await api.delete(`/pages/${pageId}/comments/${cid}`);
    toast.success("Comment deleted");
    loadComments();
  };

  return (
    <div className="pt-6 border-t space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <MessageSquare className="w-4 h-4 text-primary" /> Comments & Discussion ({comments.length})
      </div>

      <div className="space-y-3">
        {comments.map((c) => {
          const author = users.find((u) => u.id === c.author_id) || { name: "User" };
          return (
            <div key={c.id} className="p-3 border rounded-lg bg-muted/20 flex gap-3">
              <Avatar className="w-7 h-7 border shrink-0">
                <AvatarImage src={author.avatar_url} />
                <AvatarFallback className="text-[10px]">{initials(author.name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{author.name}</span>
                  <span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <div className="text-sm mt-1 leading-relaxed">{c.body}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => delComment(c.id)} className="h-6 w-6 text-muted-foreground hover:text-red-500">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          );
        })}
      </div>

      <div className="space-y-2 pt-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Add a comment to this page..."
          className="text-sm resize-none"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={submitComment} disabled={submitting || !body.trim()}>
            Post comment
          </Button>
        </div>
      </div>
    </div>
  );
}
